import { useEffect, useRef, useState } from 'react';
import { Bike, House, LocateFixed, MapPinOff, Minus, Plus, Store } from 'lucide-react';

import { googleMapsAuthFailed, loadGoogleMaps, MissingApiKeyError } from '@/lib/google-maps';
import type { Point } from '@/lib/route';
import { cn } from '@/lib/utils';

/**
 * The trip on a small live map: the shop, the door, and where the rider is.
 *
 * Ported from the admin panel's `DeliveryMap`, so dispatch and the rider look
 * at the same picture drawn the same way. Pinch, pan and zoom work — the
 * point of a live map over the old still image is that a rider can zoom into
 * the lane behind the market — but with `cooperative` gestures, so a
 * one-finger swipe still scrolls the page rather than dragging the map, and
 * nobody gets trapped inside it on a small screen.
 *
 * Places get PINS, the rider gets a round BADGE. A shop and a door are fixed points; a
 * rider is a phone reading from the last ping, and a pin would claim a
 * precision the GPS does not have.
 *
 * The dashed line is the straight line between shop and door, and the legend
 * says so. Turn-by-turn is one tap away in Google Maps; a confident route line
 * here that disagreed with the road would be worse than an obviously straight
 * one.
 *
 * Colours come from the live theme tokens, read when the map draws, so the
 * pins follow the dark switch and a rebrand like everything else in the app.
 */

type Palette = { shop: string; door: string; rider: string; outline: string };

/** Named colours as the fallback: the token layer is always there in practice. */
function palette(): Palette {
  const read = (name: string, fallback: string) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
  return {
    shop: read('--action-primary-bg', 'green'),
    door: read('--feedback-warning-text', 'darkorange'),
    rider: read('--feedback-info-text', 'royalblue'),
    outline: read('--surface-page', 'white'),
  };
}

const GLYPH = {
  /* The pharmacy cross. */
  shop: (fill: string) =>
    `<rect x="12.6" y="8.2" width="4.8" height="12.6" rx="1" fill="${fill}"/>` +
    `<rect x="8.7" y="12.1" width="12.6" height="4.8" rx="1" fill="${fill}"/>`,
  /* A house with a door cut out of it — one path, legible at 30px. */
  door: (fill: string) =>
    `<path d="M15 7.6 L22.4 14.2 V21.6 H17.4 V17.4 H12.6 V21.6 H7.6 V14.2 Z" fill="${fill}"/>`,
};

function pin(colour: string, outline: string, glyph: string): google.maps.Icon {
  // A soft ground shadow under the tip, so the pin reads as standing ON the
  // map rather than printed over it.
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="44" viewBox="-2 0 34 44">
    <ellipse cx="15" cy="40.5" rx="6" ry="2" fill="black" fill-opacity="0.28"/>
    <path d="M15 38.6C15 38.6 27.5 24.4 27.5 14.5A12.5 12.5 0 1 0 2.5 14.5C2.5 24.4 15 38.6 15 38.6Z"
          fill="${colour}" stroke="${outline}" stroke-width="2.5" stroke-linejoin="round"/>${glyph}</svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(34, 44),
    // The tip, not the middle: that pixel is the coordinate.
    anchor: new google.maps.Point(17, 39),
  };
}

/**
 * The rider: a round badge with a bike in it, inside a soft halo.
 *
 * Round rather than a teardrop on purpose — a pin says "this exact spot,
 * permanently", which is right for a shop and a door and wrong for a phone's
 * last GPS reading. The halo is the "roughly here" a location fix actually
 * means, the way every navigation app draws "you".
 */
function riderBadge(colour: string, outline: string): google.maps.Icon {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
    <circle cx="24" cy="24" r="22" fill="${colour}" fill-opacity="0.2"/>
    <circle cx="24" cy="24" r="15" fill="${colour}" stroke="${outline}" stroke-width="3"/>
    <g transform="translate(15 15) scale(0.75)" fill="none" stroke="${outline}"
       stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="18.5" cy="17.5" r="3.5"/><circle cx="5.5" cy="17.5" r="3.5"/>
      <circle cx="15" cy="5" r="1"/><path d="M12 17.5V14l-3-3 4-3 2 3h2"/>
    </g></svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(48, 48),
    // The centre IS the reading, unlike a pin's tip.
    anchor: new google.maps.Point(24, 24),
  };
}

/**
 * Frames everything plotted. One function for the first draw, the first idle,
 * a resize and the "whole trip" button, so they cannot frame differently.
 */
function fitTrip(instance: google.maps.Map | null, box: google.maps.LatLngBounds | null) {
  if (!instance || !box || box.isEmpty()) return;
  // One point, or several on top of each other: fitting a zero-size box
  // zooms all the way in, onto a street with nothing to recognise it by.
  if (box.getNorthEast().equals(box.getSouthWest())) {
    instance.setCenter(box.getCenter());
    instance.setZoom(16);
    return;
  }
  // More room on the right, where the controls sit over the map.
  instance.fitBounds(box, { top: 40, bottom: 40, left: 32, right: 64 });
}

function isDark(): boolean {
  return document.documentElement.dataset.theme === 'dark';
}

export function TripMap({
  shop,
  door,
  rider,
  className,
}: {
  shop: Point | null;
  door: Point | null;
  /** The rider's own last reported position, when the phone has one. */
  rider?: Point | null;
  className?: string;
}) {
  const host = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map | null>(null);
  const drawn = useRef<Array<google.maps.Marker | google.maps.Polyline>>([]);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [dark, setDark] = useState(isDark);

  // Follow the app's own theme switch, which is an attribute, not a media query.
  useEffect(() => {
    const observer = new MutationObserver(() => setDark(isDark()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // What `fitTrip` frames: everything plotted by the last draw.
  const bounds = useRef<google.maps.LatLngBounds | null>(null);

  useEffect(() => {
    let cancelled = false;
    let resize: ResizeObserver | null = null;
    const element = host.current;

    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !element || map.current) return;
        if (googleMapsAuthFailed()) {
          setError('The map could not load. Directions still work.');
          return;
        }
        map.current = new maps.Map(element, {
          center: { lat: 22.5726, lng: 88.3639 },
          zoom: 14,
          gestureHandling: 'cooperative',
          // Google's own controls are grey squares from another product. The
          // three this map needs are drawn below, from the app's tokens.
          disableDefaultUI: true,
          clickableIcons: false,
          colorScheme: isDark() ? 'DARK' : 'LIGHT',
          /*
           * Restaurants, hotels and museums off; streets and their names on.
           * Every labelled icon on the basemap is one more thing competing
           * with the three pins the rider actually came here for.
           */
          styles: [
            { featureType: 'poi', stylers: [{ visibility: 'off' }] },
            { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
          ],
        });
        // The first fit can run before the map knows its own size; fit again
        // once it has settled, and whenever the box changes size.
        maps.event.addListenerOnce(map.current, 'idle', () => fitTrip(map.current, bounds.current));
        resize = new ResizeObserver(() => fitTrip(map.current, bounds.current));
        resize.observe(element);
        setReady(true);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(
          cause instanceof MissingApiKeyError
            ? 'Map preview is not configured. Directions still work.'
            : 'The map could not load. Directions still work.',
        );
      });

    return () => {
      cancelled = true;
      resize?.disconnect();
      drawn.current.forEach((item) => item.setMap(null));
      drawn.current = [];
      map.current = null;
      // The Maps API has no `destroy()`, and StrictMode mounts effects twice —
      // without this the second mount builds a map inside the first one's.
      element?.replaceChildren();
    };
  }, []);

  // Everything thrown away and redrawn: three markers are cheaper to rebuild
  // than to diff, and a redraw cannot leave a stale pin behind.
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;

    drawn.current.forEach((item) => item.setMap(null));
    drawn.current = [];

    const colours = palette();
    const box = new google.maps.LatLngBounds();
    const at = (point: Point) => ({ lat: point.latitude, lng: point.longitude });

    if (shop && door) {
      drawn.current.push(
        new google.maps.Polyline({
          path: [at(shop), at(door)],
          // Dashed and quiet: this is the leg, not the route.
          strokeOpacity: 0,
          strokeColor: colours.shop,
          icons: [
            {
              icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.8, scale: 3 },
              offset: '0',
              repeat: '12px',
            },
          ],
          map: instance,
        }),
      );
    }

    if (shop) {
      box.extend(at(shop));
      drawn.current.push(
        new google.maps.Marker({
          position: at(shop),
          map: instance,
          title: 'The pharmacy',
          icon: pin(colours.shop, colours.outline, GLYPH.shop(colours.outline)),
          zIndex: 2,
        }),
      );
    }

    if (door) {
      box.extend(at(door));
      drawn.current.push(
        new google.maps.Marker({
          position: at(door),
          map: instance,
          title: 'The customer',
          icon: pin(colours.door, colours.outline, GLYPH.door(colours.outline)),
          zIndex: 2,
        }),
      );
    }

    if (rider) {
      box.extend(at(rider));
      drawn.current.push(
        new google.maps.Marker({
          position: at(rider),
          map: instance,
          title: 'You',
          icon: riderBadge(colours.rider, colours.outline),
          zIndex: 3,
        }),
      );
    }

    bounds.current = box;
    fitTrip(map.current, bounds.current);
    // Keyed on the coordinates, not the point objects: those are rebuilt on
    // every render, and redrawing the map each time would flicker the pins.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ready,
    dark,
    shop?.latitude,
    shop?.longitude,
    door?.latitude,
    door?.longitude,
    rider?.latitude,
    rider?.longitude,
  ]);

  // Only on an actual switch: re-applying the same scheme on mount re-renders
  // the basemap and can drop the fit that was just made.
  const scheme = useRef<'DARK' | 'LIGHT'>(isDark() ? 'DARK' : 'LIGHT');
  useEffect(() => {
    const next = dark ? 'DARK' : 'LIGHT';
    if (!map.current || scheme.current === next) return;
    scheme.current = next;
    map.current.setOptions({ colorScheme: next });
  }, [dark, ready]);

  function zoomBy(step: number) {
    const instance = map.current;
    if (!instance) return;
    instance.setZoom((instance.getZoom() ?? 15) + step);
  }

  if (error) {
    return (
      <p
        className={cn(
          'flex items-center gap-2 rounded-xl border border-dashed border-line-strong',
          'bg-sunken/60 px-4 py-3 text-sm leading-snug text-ink-subtle',
          className,
        )}
      >
        <MapPinOff className="size-4 shrink-0" aria-hidden="true" />
        {error}
      </p>
    );
  }

  return (
    <figure className={cn('grid gap-2', className)}>
      <div className="relative">
        <div
          ref={host}
          role="region"
          aria-label="Map of the trip from the pharmacy to the customer"
          className="h-56 overflow-hidden rounded-xl border border-line bg-sunken"
        />

        {ready ? (
          /*
           * Our own controls, not Google's: drawn from the tokens so they look
           * like the rest of the app in both themes. Top-right, clear of the
           * attribution strip Google requires along the bottom edge.
           */
          <div className="absolute inset-e-2 top-2 flex flex-col items-end gap-2">
            <MapButton label="Show the whole trip" onClick={() => fitTrip(map.current, bounds.current)}>
              <LocateFixed />
            </MapButton>
            <div className="flex flex-col overflow-hidden rounded-xl border border-line bg-raised/95 shadow-md backdrop-blur-sm">
              <MapButton label="Zoom in" grouped onClick={() => zoomBy(1)}>
                <Plus />
              </MapButton>
              <span className="mx-2 h-px bg-line" aria-hidden="true" />
              <MapButton label="Zoom out" grouped onClick={() => zoomBy(-1)}>
                <Minus />
              </MapButton>
            </div>
          </div>
        ) : null}
      </div>
      {/* Icon AND colour AND word for each marker — never colour alone. */}
      <figcaption className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-xs text-ink-subtle">
        {shop ? (
          <span className="flex items-center gap-1.5">
            <Store className="size-3.5 text-brand" aria-hidden="true" />
            Shop
          </span>
        ) : null}
        {door ? (
          <span className="flex items-center gap-1.5">
            <House className="size-3.5 text-warning-ink" aria-hidden="true" />
            Customer
          </span>
        ) : null}
        {rider ? (
          <span className="flex items-center gap-1.5">
            <Bike className="size-3.5 text-info-ink" aria-hidden="true" />
            You
          </span>
        ) : null}
        {shop && door ? <span>Dashed line is straight, not the road</span> : null}
      </figcaption>
    </figure>
  );
}

/** One control on the map: a 36px target, themed, with a real name. */
function MapButton({
  label,
  grouped = false,
  onClick,
  children,
}: {
  label: string;
  /** Inside the zoom pill, which owns the border and the background. */
  grouped?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        'grid size-9 place-items-center text-ink',
        'transition-[background-color] duration-[var(--motion-duration-fast)] ease-standard',
        'hover:bg-muted active:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/40',
        '[&_svg]:size-[1.125rem] [&_svg]:shrink-0',
        !grouped && 'rounded-xl border border-line bg-raised/95 shadow-md backdrop-blur-sm',
      )}
    >
      {children}
    </button>
  );
}
